import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  Archive, ArchiveRestore, EyeOff, Pause, MessageSquare, Globe, ShieldCheck, Loader2, Zap, AlertTriangle,
} from 'lucide-react';

// ============================================================================
// ArchiveCampaignModal — confirm step for archiving or restoring a campaign.
//
// Replaces the native window.confirm() those actions used, which customers read as
// broken ("old school"). Same family as AdminActivationWarningModal: centered card,
// icon circle, portal to <body> so it sits above the campaign settings pop-up too.
//
// Deliberately NOT red. Archiving is fully reversible, and red reads as "delete".
//
// The copy states only what the code does (see api_routes/campaigns.js archive route):
//   archive -> hidden from the list, AI paused, nothing deleted. SMS replies still get
//              answered. For the WEB CHAT campaign the route also clears the tenant's
//              widget binding (2026-09-10), and since Phase 1e a widget with no live
//              campaign shows nothing (public.web_chat_widget_state, shared by
//              /api/web-chat/config and handle-web-chat-message), so the chat disappears
//              from the website until another web campaign is picked and live.
//   restore -> back in the list, AI stays paused until Go live.
// ============================================================================

function Point({ Icon, children }) {
  return (
    <li className="flex items-start gap-3">
      <span className="mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg bg-surfox-line text-surfox-muted">
        <Icon size={14} />
      </span>
      <span className="pt-1 text-[13.5px] leading-snug text-surfox-ink">{children}</span>
    </li>
  );
}

export default function ArchiveCampaignModal({
  mode = 'archive',      // 'archive' | 'restore'
  campaignName,
  isWebChat = false,
  busy = false,
  error = '',
  onConfirm,
  onCancel,
}) {
  const isArchive = mode === 'archive';

  // Escape closes it, unless a request is in flight.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !busy) onCancel?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onCancel]);

  return createPortal(
    <div className="fixed inset-0 z-[70] flex items-center justify-center font-sans antialiased">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={() => { if (!busy) onCancel?.(); }}
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="archive-campaign-title"
        className="relative mx-4 w-full max-w-md rounded-2xl border border-surfox-border bg-white p-6 shadow-[0_24px_60px_rgba(18,30,50,.22)]"
      >
        <div className={`mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full ${isArchive ? 'bg-surfox-line text-surfox-ink' : 'bg-surfox-greenSoft text-surfox-green'}`}>
          {isArchive ? <Archive className="h-6 w-6" /> : <ArchiveRestore className="h-6 w-6" />}
        </div>

        <div className="mb-5 text-center">
          <h3 id="archive-campaign-title" className="text-[19px] font-semibold tracking-[-.2px] text-surfox-ink">
            {isArchive ? 'Archive this campaign?' : 'Restore this campaign?'}
          </h3>
          <p className="mt-1.5 truncate text-[14px] font-medium text-surfox-muted" title={campaignName}>
            {campaignName}
          </p>
        </div>

        <ul className="mb-5 space-y-3 rounded-xl border border-surfox-border bg-surfox-bg p-4">
          {isArchive ? (
            <>
              <Point Icon={EyeOff}>It disappears from your campaign list.</Point>
              <Point Icon={Pause}>Its AI is paused, so no new messages go out.</Point>
              {isWebChat ? (
                <>
                  <Point Icon={Globe}>This is your web chat campaign. Archiving disconnects it from the chat on your website.</Point>
                  {/* What an UNBOUND widget does, from the code (Phase 1e, 2026-09-10): the one rule
                      public.web_chat_widget_state says "no chat" without a live bound campaign, so
                      /api/web-chat/config returns enabled:false and handle-web-chat-message refuses
                      before recording anything. The widget is not shown at all. */}
                  <Point Icon={AlertTriangle}>
                    The chat disappears from your website. It comes back when you pick another web chat campaign
                    in Settings, Web Chat, and that campaign is live.
                  </Point>
                </>
              ) : (
                <Point Icon={MessageSquare}>Leads who text back still get an answer.</Point>
              )}
              <Point Icon={ShieldCheck}>Nothing is deleted. You can restore it anytime from Show Archived.</Point>
            </>
          ) : (
            <>
              <Point Icon={ArchiveRestore}>It goes back into your campaign list.</Point>
              <Point Icon={Zap}>Its AI stays paused. Nothing sends until you press Go live.</Point>
            </>
          )}
        </ul>

        {error && (
          <p className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-[13px] text-red-700">
            {error}
          </p>
        )}

        <div className="flex flex-col gap-2.5">
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className={`inline-flex w-full items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-[14px] font-semibold text-white transition-all disabled:cursor-wait disabled:opacity-70 ${isArchive ? 'bg-surfox-ink hover:brightness-125' : 'bg-surfox-primary hover:brightness-110'}`}
          >
            {busy ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                {isArchive ? 'Archiving...' : 'Restoring...'}
              </>
            ) : (
              <>
                {isArchive ? <Archive className="h-4 w-4" /> : <ArchiveRestore className="h-4 w-4" />}
                {isArchive ? 'Archive campaign' : 'Restore campaign'}
              </>
            )}
          </button>
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="w-full rounded-xl border border-surfox-border bg-white px-4 py-2 text-[14px] font-medium text-surfox-muted transition-colors hover:bg-surfox-line hover:text-surfox-ink disabled:opacity-50"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
